import { useState } from "react";
import './DefaultsEditor.scss';
import { DefaultsConfigWizard } from "./DefaultsConfigWizard";
import { useTenantApi } from "@/hooks";
import { useUser } from "@/contexts/UserContext";
import { ByzzerErrorModal } from "@/components/form";
import { DefaultRunConfig } from "@/types/ApiTypes";
import { triggerToast } from "@/notifications/ByzzerToast";
import { MarketRunConfigOptions, ProductRunConfigOptions, TimeRunPeriodConfigOptions, FocusKpiRunConfigOptions, DemographicsRunConfigOptions } from "@/types/RunConfigOptions";
import { ReportDatatype } from "@/types/ReportRun";

export type DefaultsEditorProps = {
    loading?: boolean;
    onCancel?: () => void;
    setLoading: (loading: boolean) => void;
    selectedDataset: ReportDatatype;
}

export const defaultConfigOptions = [
    {
        type: 'product' as any,
        title: 'Focus Product(s)',
        includeFocusBrands: true,
        includeCategories: true,
        includeCharacteristics: true,
        includeMarkets: true,
        maxCharacteristics: 5,
    } as Partial<ProductRunConfigOptions>,
    {
        type: 'market' as any,
        maxMarkets: 1,
        title: 'Focus Market',
        includeTotalUSMarkets: true,
        includeFmcgRetailers: true,
        includeSpecialityRetailers: true,
        includeGeographyMarkets: true,
    } as Partial<MarketRunConfigOptions>,
    {
        type: 'time_period' as any,
        title: 'Time Period',
    } as Partial<TimeRunPeriodConfigOptions>,
    {
        type: 'focuskpi' as any,
        title: 'Focus KPI',
    } as Partial<FocusKpiRunConfigOptions>
];

export const getDatasetConfigOptions = (dataset?: ReportDatatype) => {
    const dataset_type = dataset || 'rms';

    switch (dataset_type) {
        case 'rms':
            return defaultConfigOptions;
        
        case 'cps':
            return [
                {
                    type: 'product' as any,
                    title: 'Focus Product(s)',
                    includeFocusBrands: true,
                    includeCategories: true,
                    includeProductSubcategory: true,
                    includeCharacteristics: true,
                    includeMarkets: false,
                    maxCharacteristics: 5,
                } as Partial<ProductRunConfigOptions>,
                {
                    type: 'market' as any,
                    maxMarkets: 10,
                    title: 'Focus Market',
                    includeTotalUSMarkets: true,
                    includeFmcgRetailers: true,
                    includeSpecialityRetailers: true,
                    includeGeographyMarkets: true,
                } as Partial<MarketRunConfigOptions>,
                {
                    type: 'demographics' as any,
                    title: 'Demographics',
                    maxDemographicDimensions: 5,
                } as Partial<DemographicsRunConfigOptions>,
                {
                    type: 'time_period' as any,
                    title: 'Time Period',
                } as Partial<TimeRunPeriodConfigOptions>,
            ];
        
        case 'omni':
            return [
                {
                    type: 'product' as any,
                    title: 'Focus Product(s)',
                    includeOmniCategories: true,
                    includeOmniProducts: true,
                    maxCharacteristics: 5,
                    maxOmniProducts: 5,
                    maxCategories: 5,
                } as Partial<ProductRunConfigOptions>,
                {
                    type: 'market' as any,
                    maxMarkets: 1,
                    title: 'Focus Market',
                    includeOmniTotalMarkets: true,
                    includeOmniSubMarkets: true,
                    maxOmniTotalMarkets: 1,
                    maxOmniSubMarkets: 1,
                } as Partial<MarketRunConfigOptions>,
                {
                    type: 'time_period' as any,
                    title: 'Time Period',
                } as Partial<TimeRunPeriodConfigOptions>,
            ];
        
        case 'osh':
            return [
                {
                    type: 'product' as any,
                    title: 'Focus Product(s)',
                    includeOmniShopperCategories: true,
                    includeOmniShopperCharacteristics: true,
                    includeOmniShopperProductDimensions: true,
                    includeOmniShopperDemographics: true,
                    includeOmniShopperSubCategories: true,
                    maxCharacteristicValues: 1,
                    maxCharacteristics: 1,
                    maxSubCategories: 1,
                    maxCategories: 1,
                } as Partial<ProductRunConfigOptions>,
                {
                    type: 'market' as any,
                    maxMarkets: 1,
                    title: 'Focus Market',
                    includeOmniShopperMarkets: true,
                    maxOmniShopperMarkets: 1,
                } as Partial<MarketRunConfigOptions>,
                {
                    type: 'time_period' as any,
                    title: 'Time Period',
                } as Partial<TimeRunPeriodConfigOptions>,
                {
                    type: 'focuskpi' as any,
                    title: 'Focus KPI',
                    includeOmniShopperKPI: true,
                } as Partial<FocusKpiRunConfigOptions>
            ];
        
        default:
            return defaultConfigOptions;
    }
};

export const DefaultsEditor = ({
    onCancel,
    setLoading,
    selectedDataset,
    ...props
}: DefaultsEditorProps) => {

    const baseClassName = 'byz-defaults-editor';

    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [contentType, setContentType] = useState('warning');

    const { saveUserDefaultRunConfig, fetchReportlet, resetReportlet } = useTenantApi();
    const { refreshDefaultRunConfig } = useUser();
    
    const configOptions = getDatasetConfigOptions(selectedDataset);

    async function handleComplete(value: DefaultRunConfig) {
        setLoading(true);
        try {
            const saveResult = await saveUserDefaultRunConfig(value[selectedDataset]!, selectedDataset);
            refreshDefaultRunConfig?.(value);
            const reportletDataValues = await fetchReportlet();

            if (reportletDataValues) {
                /** Commenting this call for now because looks like it is not needed as per BYZ-15594
                 * and it is calling defaultConfigChange event just after homepageConfigChange event */                
                
                // await resetReportlet(reportletDataValues.id);
            } else {
                setWarningMessage('Your default selections have not configured. Please re-configure.');
            }
            setLoading(false);
            triggerToast({
                content: 'Default selections saved successfully',
            });
            onCancel?.();
        } catch (err) {
            // debugger
            setShowWarning(true);
            setContentType('error');
            setWarningMessage('Unable to save the preferences. Something went wrong.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <> 
            <DefaultsConfigWizard 
                onComplete={handleComplete} 
                onCancel={onCancel} 
                // @ts-ignore 
                defaultConfigOptions={configOptions}
                selectedDataset={selectedDataset}
            />
            {showWarning && (
                <ByzzerErrorModal
                    showWarning={showWarning}
                    setShowWarning={setShowWarning}
                    headerType={'none'}
                    contentType={contentType}
                    warningMessage={warningMessage}
                    size={'small'}
                />
            )}
        </>
    );

};

export default DefaultsEditor;

DefaultsEditor.displayName = 'DefaultsEditor';